// frontend/src/components/ReportDisplayModal.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-01
// Updated:      2026-09-16
// Description:  Portalled modal showing report results with themed styling, export options, Save Template support,
//               ANPR parameter context, and Excel-style client-side column filtering for previewed report rows.
//
// Responsive mobile workspace (2026-09-16):
//   • Desktop report table/filter popovers remain unchanged at lg+.
//   • Mobile uses Results / Filters / Actions workspaces instead of a horizontally-scrolling table.
//   • Mobile Results render one complete report row per card with every selected field preserved.
//   • Mobile Filters reuse the same columnFilters/filterSearch state and Excel-style value selection logic.
//   • Export, unique-plate and Save Template behaviour is unchanged; only their mobile placement changes.
//   • Report shell now sizes to ModalWrapper's content box (100%), not viewport width, preventing mobile horizontal overflow.

import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import ModalWrapper from "./ModalWrapper";
import { useTheme } from "../context/ThemeContext.jsx";
import { useAlerts } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";

const FRIENDLY_FIELD_NAMES = {
  id: "ID",
  email: "Email",
  first_name: "First Name",
  last_name: "Last Name",
  role: "Role",
  created_at: "Created At",
  last_login_at: "Last Login",
  last_active_at: "Last Active",
  timestamp: "Timestamp",

  job_id: "Job ID",
  client_name: "Client",
  status: "Status",
  username: "Username",

  run_id: "Run ID",
  frame_ts_utc: "Frame Time",
  plate_normalized: "Plate",
  plate: "Plate",
  display_plate: "Display Plate",
  yolo_conf: "Confidence",
  camera_name: "Camera",
  sighting_vehicle_make: "Sighting Make",
  sighting_vehicle_model: "Sighting Model",
  sighting_vehicle_color: "Sighting Colour",
  sighting_vehicle_category: "Sighting Category",
  vehicle_make: "Resolved Make",
  vehicle_model: "Resolved Model",
  vehicle_colour: "Resolved Colour",
  vehicle_fuel_type: "Fuel Type",
  vehicle_type_hint: "Type Hint",
  dvla_make: "DVLA Make",
  dvla_colour: "DVLA Colour",
  dvla_fuel_type: "DVLA Fuel",
  dvla_year_of_manufacture: "Year",
  dvla_tax_status: "Tax Status",
  dvla_tax_due_date: "Tax Due",
  dvla_mot_status: "MOT Status",
  dvla_mot_expiry_date: "MOT Expiry",
  dvla_engine_capacity_cc: "Engine CC",
  dvla_co2_emissions_gpkm: "CO2 g/km",
  dvla_revenue_weight_kg: "Revenue Weight KG",
  dvla_wheelplan: "Wheelplan",
  dvla_type_approval: "Type Approval",
  dvla_fetched_at: "DVLA Cached At",
  dvla_cached_at: "DVLA Applied At",
  dvla_status: "DVLA Status",
  dvla_cache_status: "DVLA Cache Status",
  sighting_count: "Sightings",
  first_seen_at: "First Seen",
  last_seen_at: "Last Seen",
  first_seen_s: "First Seen",
  last_seen_s: "Last Seen",
  dwell_s: "Dwell (s)",
  hit_count: "Hits",
  max_yolo_conf: "Max Confidence",
  avg_yolo_conf: "Avg Confidence",
  best_snapshot_url: "Plate Image",
  best_snapshot_path: "Best Snapshot Path",
  snapshot_url: "Snapshot URL",
  snapshot_path: "Snapshot Path",
  plate_secondary_url: "Plate Image URL",
  plate_secondary_path: "Plate Image Path",
  plate_b64_len: "Plate Bytes",
  overview_b64_len: "Overview Bytes",
  plate_secondary_b64_len: "Secondary Plate Bytes",
  coba_classification: "COBA Class",
  coba_confidence: "COBA Confidence",
  simplified_coba_classification: "Simple COBA",
  session_idx: "Session #",
  speed_mps_avg: "Avg Speed (m/s)",
  heading_deg_avg: "Avg Heading (deg)",
  lat_first: "Lat First",
  lon_first: "Lon First",
  lat_last: "Lat Last",
  lon_last: "Lon Last",

  camera_device_id: "Camera Device ID",
  dwell_seconds: "Dwell (s)",
  dwell_minutes: "Dwell in Min",
  max_confidence_pct: "Max Confidence %",
  avg_confidence_pct: "Avg Confidence %",
  plate_image_url: "Plate Image URL",
  plate_image_path: "Plate Image Path",
  overview_url: "Overview URL",
  overview_path: "Overview Path",

  // weaving / average speed
  pairing_id: "Pairing ID",
  pairing_type: "Pairing Type",
  route_code: "Route Code",
  route_label: "Route",
  scenario_code: "Scenario Code",
  scenario_label: "Scenario",
  weaving_confirmed: "Weaving Confirmed",
  start_label: "Start Label",
  end_label: "End Label",
  start_sighting_id: "Start Sighting ID",
  end_sighting_id: "End Sighting ID",
  start_time: "Start Time",
  end_time: "End Time",
  first_event_at: "First Event",
  last_event_at: "Last Event",
  start_camera_name: "Start Camera",
  end_camera_name: "End Camera",
  start_camera_lat: "Start Lat",
  start_camera_lon: "Start Lon",
  end_camera_lat: "End Lat",
  end_camera_lon: "End Lon",
  distance_meters: "Distance (m)",
  travel_seconds: "Travel Time (s)",
  travel_minutes: "Travel Time (min)",
  avg_travel_seconds: "Avg Travel Time (s)",
  avg_travel_minutes: "Avg Travel Time (min)",
  estimated_avg_speed_mps: "Avg Speed (m/s)",
  estimated_avg_speed_kph: "Avg Speed (kph)",
  estimated_avg_speed_mph: "Avg Speed (mph)",
  avg_speed_kph: "Avg Speed (kph)",
  avg_speed_mph: "Avg Speed (mph)",
  min_speed_kph: "Min Speed (kph)",
  max_speed_kph: "Max Speed (kph)",
  min_travel_seconds: "Min Travel Window (s)",
  weaving_max_seconds: "Weaving Window Max (s)",
  max_travel_seconds: "Max Travel Window (s)",
  start_snapshot_url: "Start Snapshot",
  end_snapshot_url: "End Snapshot",
  scenario_count: "Count",
  active_from: "Active From",
  active_to: "Active To",
};

const DATE_FIELDS = new Set([
  "created_at",
  "last_login_at",
  "last_active_at",
  "timestamp",
  "frame_ts_utc",
  "first_seen_at",
  "last_seen_at",
  "first_event_at",
  "last_event_at",
  "start_time",
  "end_time",
  "exit_time",
  "reentry_time",
  "dvla_fetched_at",
  "dvla_cached_at",
  "active_from",
  "active_to",
]);

const PERCENT_FIELDS = new Set([
  "yolo_conf",
  "avg_yolo_conf",
  "max_yolo_conf",
  "coba_confidence",
  "max_confidence_pct",
  "avg_confidence_pct",
]);

const DECIMAL_2_FIELDS = new Set([
  "dwell_s",
  "dwell_seconds",
  "dwell_minutes",
  "speed_mps_avg",
  "heading_deg_avg",
  "lat_first",
  "lon_first",
  "lat_last",
  "lon_last",
  "travel_seconds",
  "travel_minutes",
  "avg_travel_seconds",
  "avg_travel_minutes",
  "start_camera_lat",
  "start_camera_lon",
  "end_camera_lat",
  "end_camera_lon",
  "distance_meters",
  "estimated_avg_speed_mps",
  "estimated_avg_speed_kph",
  "estimated_avg_speed_mph",
  "avg_speed_kph",
  "avg_speed_mph",
  "min_speed_kph",
  "max_speed_kph",
]);

const BOOLEAN_FIELDS = new Set(["weaving_confirmed"]);
const BLANK_FILTER_KEY = "__VISION_ANPR_BLANK__";

const safeArray = (value) => (Array.isArray(value) ? value : []);

const isBlankValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

const normalizeFilterValue = (value) => {
  if (isBlankValue(value)) return BLANK_FILTER_KEY;
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatBST = (value) => {
  try {
    if (value === null || value === undefined || value === "") return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return formatInTimeZone(date, "Europe/London", "dd MMM yyyy, HH:mm");
  } catch {
    return String(value ?? "");
  }
};

const formatPercent = (value) => {
  try {
    if (value === null || value === undefined || value === "") return "";
    let n = Number(value);
    if (Number.isNaN(n)) return String(value);
    if (n >= 0 && n <= 1) n *= 100;
    return `${n.toFixed(2)}%`;
  } catch {
    return String(value ?? "");
  }
};

const formatDecimal2 = (value) => {
  try {
    if (value === null || value === undefined || value === "") return "";
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toFixed(2);
  } catch {
    return String(value ?? "");
  }
};

const formatBoolean = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (value === true || value === "true" || value === "t" || value === 1 || value === "1") {
    return "Yes";
  }
  if (value === false || value === "false" || value === "f" || value === 0 || value === "0") {
    return "No";
  }
  return String(value);
};

const formatCellValue = (field, raw) => {
  const f = String(field || "").toLowerCase();
  if (DATE_FIELDS.has(f)) return formatBST(raw);
  if (BOOLEAN_FIELDS.has(f)) return formatBoolean(raw);
  if (PERCENT_FIELDS.has(field)) return formatPercent(raw);
  if (DECIMAL_2_FIELDS.has(field)) return formatDecimal2(raw);
  return String(raw ?? "");
};

const getFilterDisplayValue = (field, valueKey) => {
  if (valueKey === BLANK_FILTER_KEY) return "(Blanks)";
  return formatCellValue(field, valueKey);
};

const renderParameterSummary = (parameters = {}) => {
  if (!parameters || typeof parameters !== "object") return null;

  const entries = Object.entries(parameters).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );

  if (!entries.length) return null;

  return (
    <div className="text-xs text-gray-600 mt-2 flex flex-wrap gap-2">
      {entries.map(([key, value]) => (
        <span
          key={key}
          className="px-2 py-1 rounded border"
          style={{ borderColor: "#e5e7eb", background: "#f9fafb" }}
        >
          <strong>{FRIENDLY_FIELD_NAMES[key] || key}:</strong> {String(value)}
        </span>
      ))}
    </div>
  );
};

const ReportDisplayModal = ({ data, fields, uid, onClose, meta = {} }) => {
  const { theme } = useTheme();
  const { triggerAlert } = useAlerts();
  const { currentUser } = useAuth();

  const {
    name = "Untitled Report",
    start_date = null,
    end_date = null,
    available_to_clients = false,
    parameters = {},
    time_slots = [],
    time_start = null,
    time_end = null,
    preview_limit = null,
    export_payload = null,
  } = meta;

  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterField, setOpenFilterField] = useState(null);
  const [filterSearch, setFilterSearch] = useState({});
  const [showUniquePlatesOnly, setShowUniquePlatesOnly] = useState(false);

  // Mobile-only presentation state. Desktop ignores these at lg+.
  const [mobileWorkspace, setMobileWorkspace] = useState("results"); // results | filters | actions
  const [mobileFilterField, setMobileFilterField] = useState("");

  const displayFields = useMemo(() => (Array.isArray(fields) ? fields : []), [fields]);
  const sourceData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const filterOptionsByField = useMemo(() => {
    const out = {};

    displayFields.forEach((field) => {
      const counts = new Map();

      sourceData.forEach((row) => {
        const key = normalizeFilterValue(row?.[field]);
        counts.set(key, (counts.get(key) || 0) + 1);
      });

      const options = Array.from(counts.entries())
        .map(([value, count]) => ({
          value,
          count,
          label: getFilterDisplayValue(field, value),
        }))
        .sort((a, b) => {
          if (a.value === BLANK_FILTER_KEY) return -1;
          if (b.value === BLANK_FILTER_KEY) return 1;
          return String(a.label).localeCompare(String(b.label), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        });

      out[field] = options;
    });

    return out;
  }, [displayFields, sourceData]);

  const activeFilterCount = useMemo(() => {
    return Object.values(columnFilters).filter((set) => set instanceof Set && set.size > 0).length;
  }, [columnFilters]);

  const filteredData = useMemo(() => {
    const activeEntries = Object.entries(columnFilters).filter(
      ([, selected]) => selected instanceof Set && selected.size > 0
    );

    const columnFilteredRows = activeEntries.length
      ? sourceData.filter((row) => {
          return activeEntries.every(([field, selected]) => {
            const valueKey = normalizeFilterValue(row?.[field]);
            return selected.has(valueKey);
          });
        })
      : sourceData;

    if (!showUniquePlatesOnly) {
      return columnFilteredRows;
    }

    const seenPlates = new Set();
    const uniqueRows = [];

    columnFilteredRows.forEach((row) => {
      const plateKey = String(
        row?.plate_normalized || row?.plate || row?.display_plate || ""
      )
        .trim()
        .toUpperCase();

      if (!plateKey) {
        uniqueRows.push(row);
        return;
      }

      if (seenPlates.has(plateKey)) {
        return;
      }

      seenPlates.add(plateKey);
      uniqueRows.push(row);
    });

    return uniqueRows;
  }, [sourceData, columnFilters, showUniquePlatesOnly]);

  const isFieldFiltered = (field) => {
    const selected = columnFilters[field];
    return selected instanceof Set && selected.size > 0;
  };

  const getFilteredOptions = (field) => {
    const search = String(filterSearch[field] || "").trim().toLowerCase();
    const options = safeArray(filterOptionsByField[field]);

    if (!search) return options;

    return options.filter((option) => {
      const label = String(option.label || "").toLowerCase();
      const value = String(option.value || "").toLowerCase();
      return label.includes(search) || value.includes(search);
    });
  };

  const mobileFilterTarget = mobileFilterField || displayFields[0] || "";
  const mobileFilterSelected =
    mobileFilterTarget && columnFilters[mobileFilterTarget] instanceof Set
      ? columnFilters[mobileFilterTarget]
      : new Set();
  const mobileVisibleOptions = mobileFilterTarget ? getFilteredOptions(mobileFilterTarget) : [];

  const getMobileRecordLabel = (row, index) => {
    const plate = row?.plate_normalized || row?.display_plate || row?.plate;
    if (plate) return String(plate);
    if (row?.pairing_id != null) return `Pairing ${row.pairing_id}`;
    if (row?.id != null) return `ID ${row.id}`;
    if (row?.run_id != null) return `Run ${row.run_id}`;
    return `Record ${index + 1}`;
  };

  const toggleColumnValue = (field, valueKey) => {
    setColumnFilters((prev) => {
      const current = prev[field] instanceof Set ? new Set(prev[field]) : new Set();

      if (current.has(valueKey)) {
        current.delete(valueKey);
      } else {
        current.add(valueKey);
      }

      const next = { ...prev };
      if (current.size) {
        next[field] = current;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const selectAllVisibleValues = (field) => {
    const visibleOptions = getFilteredOptions(field);
    setColumnFilters((prev) => {
      const current = prev[field] instanceof Set ? new Set(prev[field]) : new Set();
      visibleOptions.forEach((option) => current.add(option.value));
      return {
        ...prev,
        [field]: current,
      };
    });
  };

  const clearColumnFilter = (field) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setFilterSearch({});
    setOpenFilterField(null);
  };

  const buildFilteredCsvBlob = () => {
    const escapeCsv = (value) => {
      const s = String(value ?? "");
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const headers = displayFields.map((field) => FRIENDLY_FIELD_NAMES[field] || field);
    const lines = [headers.map(escapeCsv).join(",")];

    filteredData.forEach((row) => {
      lines.push(
        displayFields
          .map((field) => escapeCsv(formatCellValue(field, row?.[field])))
          .join(",")
      );
    });

    return new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  };

  const downloadBlob = (blob, filename) => {
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(downloadUrl);
  };

  const buildExportSubtitle = () => {
    if (start_date || end_date) {
      return start_date && end_date
        ? `${start_date} to ${end_date}`
        : start_date
        ? `From ${start_date}`
        : `To ${end_date}`;
    }
    return "";
  };

  const getSafeReportName = () =>
    String(name || "report")
      .replace(/[^a-z0-9_-]+/gi, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase() || "report";

  const exportFilteredRowsViaBackend = (format) => {
    const normalizedFormat = String(format || "").toLowerCase();
    const token = localStorage.getItem("anpr_token");
    const win = window.open();

    fetch("/api/reports/export-filtered", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        uid,
        format: normalizedFormat,
        title: name,
        subtitle: buildExportSubtitle(),
        fields: displayFields,
        data: filteredData,
        parameters,
        options: {
          unique_plates_only: showUniquePlatesOnly,
        },
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .catch(() => null)
            .then((body) => {
              const detail = body?.detail || `Failed to export: ${res.status}`;
              throw new Error(detail);
            });
        }
        return res.blob();
      })
      .then((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        if (win) {
          win.location = downloadUrl;
        } else {
          downloadBlob(blob, `${getSafeReportName()}_filtered.${normalizedFormat === "xls" ? "xlsx" : normalizedFormat}`);
        }
      })
      .catch((err) => {
        if (win) win.close();
        alert(`Failed to export filtered report.\n${err.message}`);
      });
  };

  const exportFullReportViaBackend = (format) => {
    const normalizedFormat = String(format || "").toLowerCase();

    if (!export_payload || typeof export_payload !== "object") {
      triggerAlert("Full export metadata is missing. Re-run the report preview, then export again.", "warning");
      return;
    }

    const token = localStorage.getItem("anpr_token");
    const win = window.open();

    fetch("/api/reports/export-full", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...export_payload,
        format: normalizedFormat,
        title: name,
        subtitle: buildExportSubtitle(),
        fields: displayFields,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .catch(() => null)
            .then((body) => {
              const detail = body?.detail || `Failed to export: ${res.status}`;
              throw new Error(detail);
            });
        }
        return res.blob();
      })
      .then((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        if (win) {
          win.location = downloadUrl;
        } else {
          downloadBlob(blob, `${getSafeReportName()}_full.${normalizedFormat === "xls" ? "xlsx" : normalizedFormat}`);
        }
      })
      .catch((err) => {
        if (win) win.close();
        alert(`Failed to export full report.\n${err.message}`);
      });
  };

  const handleExport = (format) => {
    const normalizedFormat = String(format || "").toLowerCase();
    const hasActiveFilters = activeFilterCount > 0 || showUniquePlatesOnly;

    if (hasActiveFilters) {
      if (!filteredData.length) {
        triggerAlert("No filtered preview rows to export", "warning");
        return;
      }

      if (normalizedFormat === "csv") {
        downloadBlob(buildFilteredCsvBlob(), `${getSafeReportName()}_filtered_preview.csv`);
        return;
      }

      exportFilteredRowsViaBackend(normalizedFormat);
      return;
    }

    exportFullReportViaBackend(normalizedFormat);
  };

  const handleSaveTemplate = async () => {
    try {
      const token = localStorage.getItem("anpr_token");
      await axios.post(
        "/api/reports/create",
        {
          name,
          fields: displayFields,
          start_date,
          end_date,
          available_to_clients,
          is_standard: false,
          created_by: currentUser?.id || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      triggerAlert("Template saved successfully", "success");
    } catch (err) {
      console.error("Failed to save template:", err);
      triggerAlert("Failed to save template", "error");
    }
  };

  const buttonStyle = {
    backgroundColor: theme?.button_action?.normal || "#008060",
    color: theme?.button_action?.text || "#ffffff",
    padding: "6px 14px",
    borderRadius: "999px",
    fontSize: "0.875rem",
    fontWeight: 600,
    transition: "background-color 0.2s",
    border: "none",
    cursor: "pointer",
  };

  const secondaryButtonStyle = {
    backgroundColor: theme?.button_general?.normal || "#374151",
    color: theme?.button_general?.text || "#ffffff",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "0.75rem",
    fontWeight: 700,
    border: "none",
    cursor: "pointer",
  };

  const actionButtons = (
    <>
      {["csv", "xls", "pdf"].map((fmt) => (
        <button
          key={fmt}
          onClick={() => handleExport(fmt)}
          style={buttonStyle}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = theme?.button_action?.normal || "#008060";
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.backgroundColor = theme?.button_action?.click || "#004f3a";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
          }}
        >
          {activeFilterCount > 0 || showUniquePlatesOnly ? `Export Preview ${fmt.toUpperCase()}` : `Export Full ${fmt.toUpperCase()}`}
        </button>
      ))}

      <button
        onClick={handleSaveTemplate}
        style={buttonStyle}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = theme?.button_action?.normal || "#008060";
        }}
        onMouseDown={(e) => {
          e.currentTarget.style.backgroundColor = theme?.button_action?.click || "#004f3a";
        }}
        onMouseUp={(e) => {
          e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
        }}
      >
        Save as Template
      </button>
    </>
  );

  return ReactDOM.createPortal(
    <ModalWrapper onClose={onClose} size="xl">
      <div
        className="w-full min-w-0"
        style={{
          width: "100%",
          maxWidth: "1400px",
          minWidth: 0,
          height: "min(900px, calc(100dvh - 24px))",
          maxHeight: "calc(100dvh - 24px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: 4,
          boxSizing: "border-box",
        }}
      >
        <div className="mb-2 shrink-0 px-3 pt-1 sm:mb-3 sm:px-4 sm:pt-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="break-words text-lg font-semibold leading-tight sm:text-xl">{name || "Report Results"}</h2>

              {(start_date || end_date) && (
                <div className="text-sm text-gray-500 mt-1">
                  {start_date && end_date
                    ? `${start_date} to ${end_date}`
                    : start_date
                    ? `From ${start_date}`
                    : `To ${end_date}`}
                </div>
              )}

              {renderParameterSummary(parameters)}

              {!!safeArray(time_slots).length && (
                <div className="text-xs text-gray-600 mt-2 flex flex-wrap gap-2">
                  {safeArray(time_slots).map((slot, index) => (
                    <span
                      key={`${slot?.label || "slot"}-${index}`}
                      className="px-2 py-1 rounded border"
                      style={{ borderColor: "#e5e7eb", background: "#fff7ed" }}
                    >
                      <strong>{slot?.label || `Slot ${index + 1}`}:</strong> {slot?.start_time}–
                      {slot?.end_time}
                    </span>
                  ))}
                </div>
              )}

              {(time_start || time_end) && (
                <div className="text-xs text-gray-600 mt-2 flex flex-wrap gap-2">
                  <span
                    className="px-2 py-1 rounded border"
                    style={{ borderColor: "#e5e7eb", background: "#fff7ed" }}
                  >
                    <strong>Time window:</strong> {time_start || "--:--"}–{time_end || "--:--"}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 hidden flex-wrap gap-2 text-sm lg:flex">
            {actionButtons}

            <label
              className="inline-flex items-center gap-2 px-3 py-[6px] rounded-full border text-xs font-bold bg-white"
              style={{ borderColor: "#d1d5db", color: "#374151" }}
              title="Keep the first visible row for each plate_normalized/plate/display_plate value"
            >
              <input
                type="checkbox"
                checked={showUniquePlatesOnly}
                onChange={(e) => setShowUniquePlatesOnly(e.target.checked)}
              />
              Show unique plates only
            </label>

            {activeFilterCount > 0 && (
              <button type="button" onClick={clearAllFilters} style={secondaryButtonStyle}>
                Clear Filters ({activeFilterCount})
              </button>
            )}
          </div>

          <div className="mt-2 break-words text-xs text-gray-500">
            Showing {filteredData.length} of {sourceData.length} preview rows
            {preview_limit && sourceData.length >= Number(preview_limit)
              ? ` · preview capped at ${Number(preview_limit).toLocaleString()} rows; full exports rerun the report uncapped`
              : ""}
            {activeFilterCount > 0 ? ` · ${activeFilterCount} active column filter(s)` : ""}
            {showUniquePlatesOnly ? " · unique plates only" : ""}
          </div>
        </div>

        <div className="shrink-0 border-y border-gray-200 bg-white px-3 py-2 lg:hidden">
          <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1">
            {[
              ["results", "Results"],
              ["filters", activeFilterCount ? `Filters ${activeFilterCount}` : "Filters"],
              ["actions", "Actions"],
            ].map(([key, label]) => {
              const active = mobileWorkspace === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMobileWorkspace(key)}
                  className="min-w-0 rounded-lg px-2 py-2 text-[11px] font-extrabold transition"
                  style={
                    active
                      ? {
                          background: "#ffffff",
                          color: "#111827",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        }
                      : {
                          background: theme?.button_general?.normal || "#374151",
                          color: theme?.button_general?.text || "#ffffff",
                        }
                  }
                  onMouseOver={(e) => {
                    if (active) return;
                    e.currentTarget.style.background = theme?.button_general?.hover || "#4b5563";
                  }}
                  onMouseOut={(e) => {
                    if (active) return;
                    e.currentTarget.style.background = theme?.button_general?.normal || "#374151";
                  }}
                  onMouseDown={(e) => {
                    if (active) return;
                    e.currentTarget.style.background = theme?.button_general?.click || "#1f2937";
                  }}
                  onMouseUp={(e) => {
                    if (active) return;
                    e.currentTarget.style.background = theme?.button_general?.hover || "#4b5563";
                  }}
                >
                  <span className="block truncate">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-3 lg:hidden">
          {mobileWorkspace === "results" ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="text-xs text-gray-600">
                  <strong className="text-gray-900">{filteredData.length}</strong> of {sourceData.length} preview rows
                </div>
                {(activeFilterCount > 0 || showUniquePlatesOnly) ? (
                  <button
                    type="button"
                    onClick={() => setMobileWorkspace("filters")}
                    style={secondaryButtonStyle}
                  >
                    Review filters
                  </button>
                ) : null}
              </div>

              {filteredData.length ? (
                filteredData.map((row, i) => (
                  <div key={`mobile-row-${i}`} className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <div
                      className="flex items-center justify-between gap-3 border-b border-gray-200 px-3 py-2"
                      style={{
                        background: theme?.accent_color || "#ff7a00",
                        color: "#ffffff",
                      }}
                    >
                      <div className="min-w-0 truncate text-sm font-extrabold">
                        {getMobileRecordLabel(row, i)}
                      </div>
                      <div className="shrink-0 text-[11px] font-bold opacity-90">
                        {i + 1} / {filteredData.length}
                      </div>
                    </div>

                    <div className="divide-y divide-gray-100">
                      {displayFields.map((field) => {
                        const raw = row?.[field];
                        const display = formatCellValue(field, raw);

                        return (
                          <div
                            key={field}
                            className="grid min-w-0 grid-cols-[minmax(104px,0.42fr)_minmax(0,1fr)] gap-3 px-3 py-2 text-xs"
                          >
                            <div className="break-words font-semibold text-gray-500">
                              {FRIENDLY_FIELD_NAMES[field] || field}
                            </div>
                            <div className="min-w-0 break-words text-gray-800 [overflow-wrap:anywhere]">
                              {display === "" ? "—" : display}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                  No rows match the active filters.
                </div>
              )}
            </div>
          ) : null}

          {mobileWorkspace === "filters" ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="flex flex-col gap-3">
                  <label
                    className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-bold text-gray-700"
                    title="Keep the first visible row for each plate_normalized/plate/display_plate value"
                  >
                    <span>Show unique plates only</span>
                    <input
                      type="checkbox"
                      checked={showUniquePlatesOnly}
                      onChange={(e) => setShowUniquePlatesOnly(e.target.checked)}
                      className="h-4 w-4 shrink-0"
                    />
                  </label>

                  <div>
                    <label className="mb-1 block text-xs font-bold text-gray-600">Filter column</label>
                    <select
                      value={mobileFilterTarget}
                      onChange={(e) => setMobileFilterField(e.target.value)}
                      className="w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      {displayFields.map((field) => (
                        <option key={field} value={field}>
                          {FRIENDLY_FIELD_NAMES[field] || field}
                          {isFieldFiltered(field)
                            ? ` (${columnFilters[field] instanceof Set ? columnFilters[field].size : 0})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {activeFilterCount > 0 ? (
                    <div>
                      <div className="mb-1 text-[11px] font-bold text-gray-500">Active filters</div>
                      <div className="flex flex-wrap gap-1.5">
                        {displayFields
                          .filter((field) => isFieldFiltered(field))
                          .map((field) => (
                            <button
                              key={`active-${field}`}
                              type="button"
                              onClick={() => setMobileFilterField(field)}
                              className="rounded-full border border-gray-300 bg-gray-50 px-2 py-1 text-[11px] font-bold text-gray-700"
                            >
                              {FRIENDLY_FIELD_NAMES[field] || field}: {columnFilters[field].size}
                            </button>
                          ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {mobileFilterTarget ? (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                  <div className="border-b border-gray-200 bg-gray-50 px-3 py-2">
                    <div className="text-xs font-extrabold text-gray-800">
                      {FRIENDLY_FIELD_NAMES[mobileFilterTarget] || mobileFilterTarget}
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                      {mobileFilterSelected.size
                        ? `${mobileFilterSelected.size} value(s) selected`
                        : "No values selected — column is not filtering"}
                    </div>
                  </div>

                  <div className="p-3">
                    <input
                      type="text"
                      value={filterSearch[mobileFilterTarget] || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilterSearch((prev) => ({ ...prev, [mobileFilterTarget]: value }));
                      }}
                      placeholder="Search values..."
                      className="mb-2 w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />

                    <div className="mb-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => selectAllVisibleValues(mobileFilterTarget)}
                        style={{ ...secondaryButtonStyle, width: "100%", padding: "8px 10px" }}
                      >
                        Select visible
                      </button>
                      <button
                        type="button"
                        onClick={() => clearColumnFilter(mobileFilterTarget)}
                        style={{ ...secondaryButtonStyle, width: "100%", padding: "8px 10px" }}
                      >
                        Clear column
                      </button>
                    </div>

                    <div className="max-h-[44dvh] overflow-y-auto rounded-lg border border-gray-200">
                      {mobileVisibleOptions.length ? (
                        mobileVisibleOptions.map((option) => {
                          const checked = mobileFilterSelected.has(option.value);
                          return (
                            <label
                              key={option.value}
                              className="flex cursor-pointer items-start gap-2 border-b border-gray-100 px-3 py-2 text-xs last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleColumnValue(mobileFilterTarget, option.value)}
                                className="mt-[2px] h-4 w-4 shrink-0"
                              />
                              <span className="min-w-0 flex-1 break-words leading-4 [overflow-wrap:anywhere]">
                                {option.label || "(Blank)"}
                              </span>
                              <span className="shrink-0 text-gray-400">{option.count}</span>
                            </label>
                          );
                        })
                      ) : (
                        <div className="p-3 text-xs text-gray-500">No values found.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
                  This report has no filterable fields.
                </div>
              )}

              {(activeFilterCount > 0 || showUniquePlatesOnly) ? (
                <button
                  type="button"
                  onClick={() => {
                    clearAllFilters();
                    setShowUniquePlatesOnly(false);
                  }}
                  style={{ ...secondaryButtonStyle, width: "100%", padding: "10px 12px" }}
                >
                  Clear all filters
                </button>
              ) : null}
            </div>
          ) : null}

          {mobileWorkspace === "actions" ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-sm font-extrabold text-gray-800">Export report</div>
                <div className="mt-1 text-xs text-gray-500">
                  {activeFilterCount > 0 || showUniquePlatesOnly
                    ? `Exports use the ${filteredData.length} filtered preview row(s).`
                    : "Exports rerun the full report using the original report parameters."}
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {["csv", "xls", "pdf"].map((fmt) => (
                    <button
                      key={`mobile-export-${fmt}`}
                      type="button"
                      onClick={() => handleExport(fmt)}
                      style={{ ...buttonStyle, width: "100%", padding: "10px 12px" }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button_action?.normal || "#008060";
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button_action?.click || "#004f3a";
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
                      }}
                    >
                      {activeFilterCount > 0 || showUniquePlatesOnly
                        ? `Export Preview ${fmt.toUpperCase()}`
                        : `Export Full ${fmt.toUpperCase()}`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-sm font-extrabold text-gray-800">Template</div>
                <div className="mt-1 text-xs text-gray-500">
                  Save the current report definition as a reusable template.
                </div>
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  style={{ ...buttonStyle, width: "100%", marginTop: 12, padding: "10px 12px" }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.button_action?.normal || "#008060";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.button_action?.click || "#004f3a";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.backgroundColor = theme?.button_action?.hover || "#006b50";
                  }}
                >
                  Save as Template
                </button>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
                <div>
                  Preview rows: <strong>{filteredData.length}</strong> / {sourceData.length}
                </div>
                <div className="mt-1 break-words">
                  Run ID: <span className="font-mono">{uid}</span>
                </div>
                {preview_limit && sourceData.length >= Number(preview_limit) ? (
                  <div className="mt-1">
                    Preview capped at {Number(preview_limit).toLocaleString()} rows; full exports rerun uncapped.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <div className="mx-4 mb-4 hidden min-h-0 flex-1 overflow-auto rounded border lg:block">
          <table className="min-w-full text-sm">
            <thead>
              <tr
                style={{
                  background: theme?.accent_color || "#ff7a00",
                  color: "#ffffff",
                }}
              >
                {displayFields.map((field) => {
                  const fieldFiltered = isFieldFiltered(field);
                  const selected = columnFilters[field] instanceof Set ? columnFilters[field] : new Set();
                  const visibleOptions = getFilteredOptions(field);

                  return (
                    <th
                      key={field}
                      className="px-4 py-2 text-left text-sm font-semibold border-b whitespace-nowrap align-top"
                      style={{ fontFamily: theme?.font_header || "sans-serif", position: "relative" }}
                    >
                      <div className="flex items-center gap-2">
                        <span>{FRIENDLY_FIELD_NAMES[field] || field}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterField((prev) => (prev === field ? null : field));
                          }}
                          title="Filter column"
                          style={{
                            border: "1px solid rgba(255,255,255,0.85)",
                            background: fieldFiltered ? "rgba(17,24,39,0.45)" : "rgba(255,255,255,0.16)",
                            color: "#ffffff",
                            borderRadius: 6,
                            padding: "1px 6px",
                            fontSize: 11,
                            fontWeight: 800,
                            lineHeight: "18px",
                          }}
                        >
                          {fieldFiltered ? `▼ ${selected.size}` : "▼"}
                        </button>
                      </div>

                      {openFilterField === field && (
                        <div
                          className="shadow-xl border rounded bg-white text-gray-800"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 8,
                            zIndex: 1000,
                            width: 280,
                            maxWidth: "80vw",
                            padding: 10,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="text-xs font-bold mb-2">
                            Filter: {FRIENDLY_FIELD_NAMES[field] || field}
                          </div>

                          <input
                            type="text"
                            value={filterSearch[field] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFilterSearch((prev) => ({ ...prev, [field]: value }));
                            }}
                            placeholder="Search values..."
                            className="w-full border rounded px-2 py-1 text-xs mb-2"
                          />

                          <div className="flex gap-2 mb-2">
                            <button
                              type="button"
                              onClick={() => selectAllVisibleValues(field)}
                              className="text-xs px-2 py-1 border rounded"
                            >
                              Select visible
                            </button>
                            <button
                              type="button"
                              onClick={() => clearColumnFilter(field)}
                              className="text-xs px-2 py-1 border rounded"
                            >
                              Clear
                            </button>
                            <button
                              type="button"
                              onClick={() => setOpenFilterField(null)}
                              className="text-xs px-2 py-1 border rounded ml-auto"
                            >
                              Close
                            </button>
                          </div>

                          <div className="max-h-64 overflow-y-auto border rounded">
                            {visibleOptions.length ? (
                              visibleOptions.map((option) => {
                                const checked = selected.has(option.value);
                                return (
                                  <label
                                    key={option.value}
                                    className="flex items-start gap-2 px-2 py-1 text-xs border-b last:border-b-0 cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => toggleColumnValue(field, option.value)}
                                      className="mt-[2px]"
                                    />
                                    <span className="flex-1 break-words leading-4">
                                      {option.label || "(Blank)"}
                                    </span>
                                    <span className="text-gray-400 shrink-0">{option.count}</span>
                                  </label>
                                );
                              })
                            ) : (
                              <div className="text-xs text-gray-500 p-2">No values found.</div>
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {filteredData.length ? (
                filteredData.map((row, i) => (
                  <tr key={i} className="even:bg-gray-50">
                    {displayFields.map((field) => {
                      const raw = row?.[field];
                      const display = formatCellValue(field, raw);

                      return (
                        <td
                          key={field}
                          className="px-4 py-2 border-b text-gray-800 align-top"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={Math.max(displayFields.length, 1)}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No rows match the active filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="hidden shrink-0 items-center justify-between px-4 pb-3 lg:flex">
          <span className="text-xs text-gray-500">
            Rows: {filteredData.length} / {sourceData.length} | Run ID: {uid}
          </span>
        </div>
      </div>
    </ModalWrapper>,
    document.body
  );
};

export default ReportDisplayModal;
